import {ContextNote} from "common/types/issue";

const CONTEXT_TAG_PATTERN = /[ \t]*\[\[ctx:(\d+)\]\]/g;
const LEADING_TAGS_PATTERN = /^([ \t]*)(?:\[\[ctx:\d+\]\][ \t]*)+/;

function isContextNote(value: unknown): value is ContextNote {
  if (!value || typeof value !== "object") {
    return false;
  }
  const note = value as Record<string, unknown>;
  return typeof note.section === "string" && typeof note.text === "string";
}

export function parseContextNotes(value: unknown): ContextNote[] {
  let list = value;
  if (typeof value === "string") {
    try {
      list = JSON.parse(value);
    } catch (err) {
      return [];
    }
  }
  return Array.isArray(list) ? list.filter(isContextNote) : [];
}

function moveLineTagsToEnd(line: string): string {
  if (!line.includes("[[ctx:") || line.trimStart().startsWith("|")) {
    return line;
  }
  const indexes = Array.from(line.matchAll(CONTEXT_TAG_PATTERN), match =>
    Number(match[1]),
  );
  if (!indexes.length) {
    return line;
  }
  const unique = Array.from(new Set(indexes)).sort((a, b) => a - b);
  const withoutTags = line
    .replace(LEADING_TAGS_PATTERN, "$1")
    .replace(CONTEXT_TAG_PATTERN, "")
    .trimEnd();
  const tags = unique.map(index => `[[ctx:${index}]]`).join("");
  return `${withoutTags} ${tags}`;
}

export function moveContextTagsToLineEnd(text: string): string {
  if (!text.includes("[[ctx:")) {
    return text;
  }
  return text.split("\n").map(moveLineTagsToEnd).join("\n");
}

export function replaceContextTagsWithChips(
  text: string,
  contextNotes: ContextNote[],
): string {
  if (!text.includes("[[ctx:")) {
    return text;
  }
  const moved = moveContextTagsToLineEnd(text);
  return moved.replace(CONTEXT_TAG_PATTERN, (match, index: string) => {
    const note = contextNotes[Number(index) - 1];
    if (!note) {
      return "";
    }
    const encoded = encodeURIComponent(JSON.stringify(note));
    return `<ContextChip data="${encoded}"></ContextChip>`;
  });
}

export function stripContextTags(text: string): string {
  if (typeof text !== "string" || !text.includes("[[ctx:")) {
    return text;
  }
  return text
    .split("\n")
    .map(line =>
      line.replace(LEADING_TAGS_PATTERN, "$1").replace(CONTEXT_TAG_PATTERN, ""),
    )
    .join("\n");
}
